import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, MessageCircle, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { publicAPI } from '../services/api';
import { resolveImageUrl } from '../utils/imageUrl';
import { useSiteData } from '../contexts/SiteDataContext';
import { formatWhatsAppLink, getDefaultQuoteMessage } from '../utils/whatsapp';

const Products = () => {
  const navigate = useNavigate();
  const { categories } = useSiteData();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    publicAPI.getProducts().then(data => { setProducts(data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const filteredProducts = products.filter((product) => {
    const matchesSearch = !searchTerm || product.name.toLowerCase().includes(searchTerm.toLowerCase()) || product.code.toLowerCase().includes(searchTerm.toLowerCase()) || (product.shortDescription || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleQuoteClick = (e, product) => {
    e.stopPropagation();
    window.open(formatWhatsAppLink(getDefaultQuoteMessage(product.code, product.name)), '_blank');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <section className="bg-gradient-to-r from-[#055509] to-[#0a7a0f] text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Catálogo de Produtos</h1>
          <p className="text-xl text-gray-200">Explore nosso catálogo completo de componentes hidráulicos técnicos</p>
        </div>
      </section>

      <section className="py-8 bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <Input type="text" placeholder="Buscar por código, nome ou descrição..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 h-12 border-gray-300" />
            </div>
            <div className="md:w-64">
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="h-12 border-gray-300"><Filter size={20} className="mr-2" /><SelectValue placeholder="Todas as categorias" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as categorias</SelectItem>
                  {categories.map((cat) => <SelectItem key={cat.id || cat.slug} value={cat.name}>{cat.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-4 text-sm text-gray-600">Mostrando <span className="font-semibold text-[#055509]">{filteredProducts.length}</span> produtos</div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4">
          {loading ? (
            <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#055509]" /></div>
          ) : filteredProducts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProducts.map((product) => (
                <Card key={product.id} className="group cursor-pointer hover:shadow-2xl transition-all duration-300 border-2 border-gray-100 hover:border-[#FFB025] overflow-hidden" onClick={() => navigate(`/produto/${product.id}`)}>
                  <div className="relative h-64 overflow-hidden bg-gray-100">
                    <img src={resolveImageUrl(product.mainImage || product.images?.[0])} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" onError={(e) => { e.target.onerror = null; e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDQwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjQwMCIgaGVpZ2h0PSI0MDAiIGZpbGw9IiNFNUU3RUIiLz48dGV4dCB4PSIyMDAiIHk9IjIwMCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgZm9udC1mYW1pbHk9InNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTYiIGZpbGw9IiM5Q0EzQUYiPlNlbSBJbWFnZW08L3RleHQ+PC9zdmc+'; }} />
                    <div className="absolute top-4 left-4"><Badge className="bg-[#055509] text-white">{product.category}</Badge></div>
                  </div>
                  <CardContent className="p-6">
                    <div className="text-sm font-mono text-[#FFB025] font-semibold mb-2">{product.code}</div>
                    <h3 className="text-xl font-bold text-[#055509] mb-3 group-hover:text-[#FFB025] transition-colors line-clamp-2">{product.name}</h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">{product.shortDescription}</p>
                    <div className="mb-4">
                      <div className="text-xs font-semibold text-gray-500 mb-2">APLICAÇÕES:</div>
                      <div className="flex flex-wrap gap-2">
                        {(product.applications || []).slice(0, 2).map((app, idx) => <span key={idx} className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded">{app}</span>)}
                        {(product.applications || []).length > 2 && <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded">+{product.applications.length - 2}</span>}
                      </div>
                    </div>
                    <div className="flex gap-2 pt-4 border-t border-gray-100">
                      <Button size="sm" onClick={(e) => handleQuoteClick(e, product)} className="flex-1 bg-[#FFB025] text-[#055509] hover:bg-[#055509] hover:text-white font-semibold">
                        <MessageCircle size={16} className="mr-2" />Solicitar Cotação
                      </Button>
                      <Button size="sm" variant="outline" className="border-[#055509] text-[#055509] hover:bg-[#055509] hover:text-white"><ArrowRight size={16} /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <Search size={64} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-2xl font-bold text-gray-600 mb-2">Nenhum produto encontrado</h3>
              <p className="text-gray-500 mb-6">Tente ajustar os filtros ou entre em contato conosco</p>
              <Button onClick={() => window.open(formatWhatsAppLink(), '_blank')} className="bg-[#FFB025] text-[#055509] hover:bg-[#055509] hover:text-white">
                <MessageCircle className="mr-2" size={20} />Falar com Especialista
              </Button>
            </div>
          )}
        </div>
      </section>
      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default Products;
